import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useDispatch, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { updateClientFeature } from "src/ui/containers/clients/api";
import { useClient } from "src/ui/containers/clients/hooks/useClient";
import { useClientFeatures } from "src/ui/containers/clients/hooks/useClientFeatures";

import { getStatusIndicatorData } from "@dashboard/shared/subscription/subscriptionStatus";
import { closeDetails, selectClientDetails, selectPaneTab, setPaneTab } from "@devices/store";
import { TabContext, TabList } from "@mui/lab";
import { Box, Typography, useTheme } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { LoadingBox } from "@shared/Loading";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import { PaneElement } from "../shared/PaneElement";
import { PaneTabPanel } from "../shared/PaneTabPanel";
import { useStyle } from "../shared/style";
import { Tab } from "../shared/Tab";

import LimitsTab from "./LimitsTab/LimitsTab";
import WorkspaceTab from "./WorkspaceTab/WorkspaceTab";

const clientTabs = ["WORKSPACE", "FEATURES"];

export const ClientSidePanel = (props: { refetchClientsList?: () => Promise<void> }) => {
    const { t } = useTranslation(["common", "sidepanels", "modals"]);
    const dispatch = useDispatch();
    const theme = useTheme();
    const { classes } = useStyle();
    const currentWorkspaceId = useSelector(selectCurrentWorkspaceId);
    const details = useSelector(selectClientDetails);
    const value = useSelector(selectPaneTab);
    const [localClientFeatures, setLocalClientFeatures] = useState<API.Feature[] | null>(null);
    const isClientTab = clientTabs.includes(value);

    const {
        data: selectedClient,
        loading,
        refetch,
    } = useClient(
        currentWorkspaceId && details?.selectedId ? { workspaceId: currentWorkspaceId, clientId: details.selectedId } : null,
    );
    const { data: clientFeatures, loading: featuresLoading } = useClientFeatures(
        currentWorkspaceId && details?.selectedId ? { workspaceId: currentWorkspaceId, clientId: details.selectedId } : null,
    );

    const onChange = (_event: React.ChangeEvent<object>, tabTitle: (typeof clientTabs)[number]) => {
        dispatch(setPaneTab(tabTitle));
    };

    const handleAddonToggle = async (limitType: string, newValue: number) => {
        if (!localClientFeatures || !currentWorkspaceId || !details?.selectedId) {
            return;
        }

        try {
            await updateClientFeature({
                workspaceId: currentWorkspaceId,
                clientId: details.selectedId,
                type: limitType,
                value: newValue,
            });

            const updatedFeatures = localClientFeatures.map((feature) => {
                if (feature.feature === limitType) {
                    return { ...feature, used: newValue };
                }
                return feature;
            });

            setLocalClientFeatures(updatedFeatures);
            showSuccessToast(t("modals:changeLimits.toast.addonUpdatedSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("modals:changeLimits.toast.addonUpdateFailed"));
        }
    };

    const handleLimitUpdate = (limitType: string, newValue: number) => {
        if (!localClientFeatures) {
            return;
        }

        const updatedFeatures = localClientFeatures.map((feature) => {
            if (feature.feature === limitType) {
                return { ...feature, max: newValue };
            }
            return feature;
        });

        setLocalClientFeatures(updatedFeatures);
    };

    useEffect(() => {
        setLocalClientFeatures(clientFeatures);
    }, [clientFeatures]);

    React.useEffect(() => {
        if (!isClientTab) {
            dispatch(setPaneTab("WORKSPACE"));
        }
    }, [isClientTab, dispatch]);

    if (!isClientTab) {
        return null;
    }
    if (!selectedClient || loading || featuresLoading) {
        return <LoadingBox className={classes.loadingBox} />;
    }

    const statusIndicator = selectedClient?.subscription?.status
        ? getStatusIndicatorData(selectedClient.subscription.status, theme)
        : {
              tooltip: i18next.t("pages:clientsOverviewPage.table.tooltip.unknownStatus"),
              color: theme.palette.emteria.text.secondary,
          };

    return (
        <TabContext value={value}>
            <PaneElement.HeaderBox>
                <Box className={classes.headerBox}>
                    <Box className={classes.licenseSummaryBox}>
                        <Tooltip title={statusIndicator.tooltip}>
                            <Box className={classes.licenseSummaryIndicator} style={{ backgroundColor: statusIndicator.color }} />
                        </Tooltip>
                        <Typography variant="h6" className={classes.panelTitle}>
                            {selectedClient?.organizationName || t("common:label.details")}
                        </Typography>
                    </Box>
                </Box>

                <Box className={classes.buttonBox}>
                    <SecondaryButton onClick={() => dispatch(closeDetails())}>
                        <i className="fas fa-times" />
                    </SecondaryButton>
                </Box>
            </PaneElement.HeaderBox>

            <TabList onChange={onChange} variant="scrollable" scrollButtons>
                <Tab
                    value="WORKSPACE"
                    label={t("sidepanels:clientsPanel.workspaceTab.title")}
                    icon={<i className="fas fa-tachometer-alt" />}
                />
                <Tab value="FEATURES" label={t("common:limits.subscriptionLimits")} icon={<i className="fas fa-file" />} />
            </TabList>

            <Box className={classes.contentBox}>
                <PaneTabPanel value="WORKSPACE">
                    <WorkspaceTab workspace={selectedClient} refetch={refetch} refetchClientsList={props.refetchClientsList} />
                </PaneTabPanel>

                <PaneTabPanel value="FEATURES">
                    <LimitsTab
                        clientFeatures={localClientFeatures}
                        clientId={details?.selectedId}
                        onLimitChange={handleLimitUpdate}
                        onAddonToggle={handleAddonToggle}
                    />
                </PaneTabPanel>
            </Box>
        </TabContext>
    );
};
